import React, { Component, useEffect } from 'react';
import { useAppShell } from '../app-shell';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { Alert } from '@mui/lab';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import ChatInterface from './ChatInterface';
import ChatCapabilities from './ChatCapabilities';
import chatService from '../../services/chat-service';
import { auth } from '../../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { currentPageLoginPath } from '../../lib/login-redirect';

type SnackbarSeverity = 'error' | 'warning' | 'info' | 'success';

interface ToolCall {
  function: { name: string };
}

interface ToolResult {
  success?: boolean;
  result?: unknown[];
}

interface Message {
  role: string;
  content: string;
  timestamp: string;
  id: string;
  tool_calls?: ToolCall[];
  tool_results?: ToolResult[];
  usage?: unknown;
}

interface CapabilitiesData {
  provider?: string;
  capabilities: string[];
  tools?: { name: string; description?: string }[];
}

interface HealthStatus {
  success: boolean;
}

interface State {
  loading: boolean;
  error: string | null;
  capabilities: CapabilitiesData | null;
  healthStatus: HealthStatus | null;
  conversation: Message[];
  snackbar: {
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
  };
}

const styles = (theme: Theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[50],
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    minHeight: '80vh',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  header: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    height: '200px',
  },
});

interface Props extends WithStyles<typeof styles> {}

class ChatPage extends Component<Props, State> {
  private unsubscribeAuth: (() => void) | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: true,
      error: null,
      capabilities: null,
      healthStatus: null,
      conversation: [],
      snackbar: {
        open: false,
        message: '',
        severity: 'info',
      },
    };
  }

  override async componentDidMount(): Promise<void> {
    console.log('[ChatPage] Component mounted, checking auth state...');

    this.unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      console.log(
        '[ChatPage] Auth state changed:',
        user ? { uid: user.uid, email: user.email, emailVerified: user.emailVerified } : 'No user',
      );

      if (user && !user.isAnonymous) {
        console.log('[ChatPage] User authenticated, initializing chat...');
        void this.initializeChat();
      } else {
        console.log('[ChatPage] No authenticated user found, redirecting...');
        this.setState({
          loading: false,
          error: 'Authentication required. Redirecting to login...',
        });

        setTimeout(() => {
          window.location.href = currentPageLoginPath();
        }, 2000);
      }
    });
  }

  override componentWillUnmount(): void {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }

  async initializeChat(): Promise<void> {
    try {
      console.log('[ChatPage] Starting chat initialization...');
      this.setState({ loading: true, error: null });

      console.log('[ChatPage] Checking health status...');
      const healthStatus = (await chatService.checkHealth()) as HealthStatus;
      console.log('[ChatPage] Health status received:', healthStatus);

      if (!healthStatus.success) {
        throw new Error('Chat service is not available');
      }

      console.log('[ChatPage] Getting capabilities...');
      const capabilities = (await chatService.getCapabilities()) as CapabilitiesData;
      console.log('[ChatPage] Capabilities received:', capabilities);

      this.setState({
        loading: false,
        healthStatus,
        capabilities,
        conversation: [
          {
            role: 'assistant',
            content: `Hello! I'm your Stash agent. I can help you search for information about stashers, stashcards, and vendors. Here's what I can do:\n\n${(capabilities.capabilities ?? []).map((cap) => `• ${cap}`).join('\n')}\n\nWhat would you like to search for?`,
            timestamp: new Date().toISOString(),
            id: 'welcome',
          },
        ],
      });

      console.log('[ChatPage] Chat initialization completed successfully');
    } catch (error) {
      console.error('[ChatPage] Failed to initialize chat:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.setState({
        loading: false,
        error: message,
      });
    }
  }

  handleSendMessage = async (messageText: string): Promise<void> => {
    try {
      console.log('[ChatPage] Sending message:', messageText);
      const userMessage: Message = {
        role: 'user',
        content: messageText,
        timestamp: new Date().toISOString(),
        id: Math.random().toString(36).substr(2, 9),
      };

      const newConversation = [...this.state.conversation, userMessage];
      this.setState({ conversation: newConversation });

      const apiMessages = newConversation.filter((msg) => msg.id !== 'welcome');
      console.log('[ChatPage] Sending API messages:', apiMessages);

      const response = (await chatService.sendMessage(
        apiMessages as Parameters<typeof chatService.sendMessage>[0],
      )) as {
        message: string;
        timestamp: string;
        tool_calls?: ToolCall[];
        tool_results?: ToolResult[];
        usage?: unknown;
      };
      console.log('[ChatPage] Received response:', response);

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.message,
        timestamp: response.timestamp,
        id: Math.random().toString(36).substr(2, 9),
        ...(response.tool_calls !== undefined ? { tool_calls: response.tool_calls } : {}),
        ...(response.tool_results !== undefined ? { tool_results: response.tool_results } : {}),
        ...(response.usage !== undefined ? { usage: response.usage } : {}),
      };

      this.setState({
        conversation: [...newConversation, assistantMessage],
      });
    } catch (error) {
      console.error('[ChatPage] Error sending message:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.showSnackbar(message, 'error');
    }
  };

  handleClearConversation = (): void => {
    this.setState({
      conversation: [
        {
          role: 'assistant',
          content: `Conversation cleared! I'm ready to help you search for information about stashers, stashcards, and vendors. What would you like to find?`,
          timestamp: new Date().toISOString(),
          id: 'welcome-clear',
        },
      ],
    });
  };

  showSnackbar = (message: string, severity: SnackbarSeverity = 'info'): void => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  handleCloseSnackbar = (): void => {
    this.setState({
      snackbar: { ...this.state.snackbar, open: false },
    });
  };

  override render(): React.JSX.Element {
    const { classes } = this.props;
    const { loading, error, conversation, capabilities, snackbar } = this.state;

    if (loading) {
      return (
        <Container maxWidth="lg" className={classes.root}>
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        </Container>
      );
    }

    if (error) {
      return (
        <Container maxWidth="lg" className={classes.root}>
          <Paper className={classes.paper}>
            <Typography variant="h6" color="error">
              Chat Service Error
            </Typography>
            <Typography variant="body1" style={{ marginTop: 16 }}>
              {error}
            </Typography>
          </Paper>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" className={classes.root}>
        <Paper className={classes.paper}>
          <Box className={classes.header}>
            <Typography variant="h4" gutterBottom>
              Stash Chat Agent
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Search and explore Stash using natural language
            </Typography>
          </Box>

          <Box className={classes.content}>
            <ChatInterface
              conversation={conversation}
              onSendMessage={this.handleSendMessage}
              onClearConversation={this.handleClearConversation}
            />

            {capabilities && <ChatCapabilities capabilities={capabilities} />}
          </Box>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.handleCloseSnackbar}
        >
          <Alert onClose={this.handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }
}

const ChatPageStyled = withStyles(styles)(ChatPage);

function ChatPageWithBreadcrumb() {
  const { setBreadcrumb } = useAppShell();
  useEffect(() => {
    setBreadcrumb(['Ask STASH']);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb]);
  return <ChatPageStyled />;
}

export default ChatPageWithBreadcrumb;
